// components/EnhancedSupplierReturns.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  Container,
  TablePagination
} from "@mui/material";
import {
  Add,
  ArrowBack,
  CheckCircle,
  Pending,
  Cancel,
  Search,
  Inventory,
  TrendingDown,
  Receipt,
  Visibility,
  Edit,
  PlaylistAddCheck,
  LocalShipping,
} from "@mui/icons-material";
import { suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
  </div>
);

const conditionTypes = [
  { value: "damaged", label: "Damaged", color: "error" },
  { value: "expired", label: "Expired", color: "warning" },
  { value: "wrong_item", label: "Wrong Item", color: "info" },
  { value: "quality_issue", label: "Quality Issue", color: "secondary" },
  { value: "excess_stock", label: "Excess Stock", color: "primary" },
  { value: "other", label: "Other", color: "default" },
];

export const SupplierReturns = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [returns, setReturns] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [returnDialog, setReturnDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [statistics, setStatistics] = useState(null);

  const [searchParams, setSearchParams] = useState({
    medicineName: "",
    batchNumber: "",
    poNumber: "",
  });

  const [newReturn, setNewReturn] = useState({
    items: [],
    returnDate: new Date().toISOString().split("T")[0],
    reason: "",
    notes: "",
    purchaseOrder: "",
  });

  const [statusUpdate, setStatusUpdate] = useState({
    status: "",
    notes: "",
    creditNoteNumber: "",
  });

  useEffect(() => {
    fetchReturns();
    fetchReturnStatistics();
  }, [id]);

  const fetchReturns = async () => {
    try {
      const response = await suppliersAPI.getReturns(id);
      setReturns(response.data.data.returns || []);
    } catch (error) {
      enqueueSnackbar("Error fetching returns", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchReturnStatistics = async () => {
    try {
      const response = await suppliersAPI.getReturnStatistics(id);
      setStatistics(response.data.data);
    } catch (error) {
      console.error("Error fetching return statistics:", error);
    }
  };

  const searchBatches = async () => {
    setSearchLoading(true);
    try {
      const response = await suppliersAPI.searchBatchesForReturn(
        id,
        searchParams
      );
      setAvailableBatches(response.data.data.batches || []);
    } catch (error) {
      enqueueSnackbar("Error searching batches", { variant: "error" });
      setAvailableBatches([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBatchSelection = (batch, selected) => {
    if (selected) {
      setSelectedBatches((prev) => [
        ...prev,
        {
          ...batch,
          returnQuantity: 1,
          reason: "",
          condition: "damaged",
        },
      ]);
    } else {
      setSelectedBatches((prev) =>
        prev.filter((b) => b.batchId !== batch.batchId)
      );
    }
  };

  const updateSelectedBatch = (batchId, field, value) => {
    setSelectedBatches((prev) =>
      prev.map((batch) =>
        batch.batchId === batchId ? { ...batch, [field]: value } : batch
      )
    );
  };

  const handleCreateReturn = async () => {
    try {
      console.log("Selected Batches for Return:", selectedBatches);

      const purchaseOrder = selectedBatches[0]?.purchaseOrder?._id || null;

      const returnData = {
        returnDate: newReturn.returnDate,
        reason: newReturn.reason,
        notes: newReturn.notes,
        purchaseOrder: purchaseOrder,
        items: selectedBatches.map((batch) => ({
          medicine: batch.medicine._id,
          batchNumber: batch.batchNumber,
          batchId: batch.batchId,
          quantity: parseInt(batch.returnQuantity),
          unitCost: parseFloat(batch.costPrice),
          totalCredit: parseFloat(batch.returnQuantity * batch.costPrice),
          reason: batch.reason,
          condition: batch.condition,
          purchaseOrder: batch.purchaseOrder?._id || null,
        })),
      };

      console.log("Sending return data:", returnData);

      const response = await suppliersAPI.createReturn(id, returnData);

      enqueueSnackbar("Return created successfully", { variant: "success" });
      setReturnDialog(false);
      resetReturnForm();
      fetchReturns();
      fetchReturnStatistics();
    } catch (error) {
      console.error("Error details:", error.response?.data);
      enqueueSnackbar(
        error.response?.data?.message || "Error creating return",
        { variant: "error" }
      );
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await suppliersAPI.updateReturnStatus(
        id,
        selectedReturn._id,
        statusUpdate
      );
      enqueueSnackbar(`Return ${statusUpdate.status} successfully`, {
        variant: "success",
      });
      setStatusDialog(false);
      setSelectedReturn(null);
      setStatusUpdate({ status: "", notes: "", creditNoteNumber: "" });
      fetchReturns();
      fetchReturnStatistics();
    } catch (error) {
      enqueueSnackbar("Error updating return status", { variant: "error" });
    }
  };

  const resetReturnForm = () => {
    setSelectedBatches([]);
    setSearchParams({ medicineName: "", batchNumber: "", poNumber: "" });
    setAvailableBatches([]);
    setNewReturn({
      items: [],
      returnDate: new Date().toISOString().split("T")[0],
      reason: "",
      notes: "",
      purchaseOrder: "",
    });
  };

  const calculateReturnTotal = () => {
    return selectedBatches.reduce(
      (sum, batch) => sum + batch.returnQuantity * batch.costPrice,
      0
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      pending: "warning",
      approved: "info",
      completed: "success",
      rejected: "error",
      cancelled: "default",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      draft: <Pending />,
      pending: <Pending />,
      approved: <PlaylistAddCheck />,
      completed: <CheckCircle />,
      rejected: <Cancel />,
      cancelled: <Cancel />,
    };
    return icons[status] || <Pending />;
  };

  const handleViewReturn = (returnRecord) => {
    setSelectedReturn(returnRecord);
    setViewDialog(true);
  };

  const handleStatusUpdate = (returnRecord) => {
    setSelectedReturn(returnRecord);
    setStatusUpdate({ status: "", notes: "", creditNoteNumber: "" });
    setStatusDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
        <Container maxWidth="xl" sx={{ py: 5 }}>
          <LinearProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/hospital/pharmacy/suppliers")}
              variant="outlined"
              sx={{ borderRadius: 2, fontSize: '1rem' }}
            >
              Back to Suppliers
            </Button>
            <Typography variant="h4" fontWeight="bold" fontSize={{ xs: '1.75rem', md: '2.125rem' }}>
              Supplier Returns
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setReturnDialog(true)}
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2, 
              transition: "0.3s", 
              "&:hover": { transform: "scale(1.03)" },
              fontSize: '1rem',
              py: 1.5
            }}
          >
            Create Return
          </Button>
        </Box>

        {/* Statistics Cards */}
        {statistics && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Inventory
                    sx={{ fontSize: 48, color: "primary.main", mb: 1 }}
                  />
                  <Typography variant="h4" fontWeight="bold" color="primary.main" fontSize={{ xs: '1.75rem', md: '2.125rem' }}>
                    {statistics.statistics.totalReturns}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontSize="1.1rem">
                    Total Returns
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Receipt sx={{ fontSize: 48, color: "warning.main", mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="warning.main" fontSize={{ xs: '1.75rem', md: '2.125rem' }}>
                    {statistics.statistics.pendingReturns}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontSize="1.1rem">
                    Pending Returns
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <TrendingDown
                    sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                  />
                  <Typography variant="h4" fontWeight="bold" color="success.main" fontSize={{ xs: '1.75rem', md: '2.125rem' }}>
                    ₹{statistics.statistics.totalCreditAmount?.toLocaleString()}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontSize="1.1rem">
                    Total Credit
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <CheckCircle sx={{ fontSize: 48, color: "info.main", mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="info.main" fontSize={{ xs: '1.75rem', md: '2.125rem' }}>
                    {statistics.statistics.completedReturns}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontSize="1.1rem">
                    Completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <LocalShipping
                    sx={{ fontSize: 48, color: "secondary.main", mb: 1 }}
                  />
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="secondary.main"
                    fontSize={{ xs: '1.75rem', md: '2.125rem' }}
                  >
                    ₹{statistics.statistics.averageReturnValue?.toLocaleString()}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" fontSize="1.1rem">
                    Avg Return Value
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Returns Tabs */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
            >
              <Tab label="All Returns" sx={{ fontSize: '1rem', fontWeight: 600 }} />
              <Tab label="Draft" sx={{ fontSize: '1rem', fontWeight: 600 }} />
              <Tab label="Pending" sx={{ fontSize: '1rem', fontWeight: 600 }} />
              <Tab label="Approved" sx={{ fontSize: '1rem', fontWeight: 600 }} />
              <Tab label="Completed" sx={{ fontSize: '1rem', fontWeight: 600 }} />
            </Tabs>
          </CardContent>
        </Card>

        {/* Returns Table */}
        <ReturnTable
          returns={returns}
          tabValue={tabValue}
          onView={handleViewReturn}
          onStatusUpdate={handleStatusUpdate}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
        />

        {/* Create Return Dialog */}
        <Dialog
          open={returnDialog}
          onClose={() => setReturnDialog(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>
            <Typography variant="h5" fontWeight="bold" fontSize="1.5rem">
              Create Supplier Return
            </Typography>
            <Typography variant="body1" color="text.secondary" fontSize="1.1rem">
              Search and select batches purchased from this supplier to return
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Search Section */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontSize="1.25rem">
                      Search Purchased Batches
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Medicine Name"
                          value={searchParams.medicineName}
                          onChange={(e) =>
                            setSearchParams((prev) => ({
                              ...prev,
                              medicineName: e.target.value,
                            }))
                          }
                          placeholder="Search by medicine name..."
                          sx={{ borderRadius: 2 }}
                          InputProps={{
                            sx: { fontSize: '1rem' }
                          }}
                          InputLabelProps={{
                            sx: { fontSize: '1rem' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Batch Number"
                          value={searchParams.batchNumber}
                          onChange={(e) =>
                            setSearchParams((prev) => ({
                              ...prev,
                              batchNumber: e.target.value,
                            }))
                          }
                          placeholder="Search by batch number..."
                          sx={{ borderRadius: 2 }}
                          InputProps={{
                            sx: { fontSize: '1rem' }
                          }}
                          InputLabelProps={{
                            sx: { fontSize: '1rem' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="PO Number"
                          value={searchParams.poNumber}
                          onChange={(e) =>
                            setSearchParams((prev) => ({
                              ...prev,
                              poNumber: e.target.value,
                            }))
                          }
                          placeholder="Search by PO number..."
                          sx={{ borderRadius: 2 }}
                          InputProps={{
                            sx: { fontSize: '1rem' }
                          }}
                          InputLabelProps={{
                            sx: { fontSize: '1rem' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          startIcon={<Search />}
                          onClick={searchBatches}
                          disabled={searchLoading}
                          sx={{ 
                            borderRadius: 2,
                            fontSize: '1rem',
                            py: 1.5
                          }}
                        >
                          {searchLoading ? "Searching..." : "Search Batches"}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Available Batches */}
              {availableBatches.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom fontSize="1.25rem">
                        Available Batches ({availableBatches.length})
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox"></TableCell>
                              <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>Medicine</TableCell>
                              <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>Batch Number</TableCell>
                              <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>PO Number</TableCell>
                              <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 600 }}>Available Qty</TableCell>
                              <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 600 }}>Cost Price</TableCell>
                              <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>Expiry Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {availableBatches.map((batch) => (
                              <TableRow key={batch.batchId} hover>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={selectedBatches.some(
                                      (b) => b.batchId === batch.batchId
                                    )}
                                    onChange={(e) =>
                                      handleBatchSelection(
                                        batch,
                                        e.target.checked
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle1" fontSize="1rem">
                                    {batch.medicine?.name}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    fontSize="0.9rem"
                                  >
                                    {batch.medicine?.genericName} •{" "}
                                    {batch.medicine?.strength}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ fontSize: '1rem' }}>{batch.batchNumber}</TableCell>
                                <TableCell sx={{ fontSize: '1rem' }}>
                                  {batch.purchaseOrder?.poNumber}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '1rem' }}>
                                  {batch.availableQuantity}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '1rem' }}>
                                  ₹{batch.costPrice?.toFixed(2)}
                                </TableCell>
                                <TableCell sx={{ fontSize: '1rem' }}>
                                  {new Date(
                                    batch.expiryDate
                                  ).toLocaleDateString()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Selected Batches for Return */}
              {selectedBatches.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom fontSize="1.25rem">
                        Selected Items for Return ({selectedBatches.length})
                      </Typography>
                      {selectedBatches.map((batch, index) => (
                        <Card
                          key={batch.batchId}
                          sx={{ mb: 2, p: 2, bgcolor: "background.default", borderRadius: 2 }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <Typography variant="subtitle1" fontSize="1rem">
                                {batch.medicine?.name}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" fontSize="0.9rem">
                                Batch: {batch.batchNumber}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <TextField
                                fullWidth
                                type="number"
                                label="Return Qty"
                                value={batch.returnQuantity}
                                onChange={(e) =>
                                  updateSelectedBatch(
                                    batch.batchId,
                                    "returnQuantity",
                                    Math.min(
                                      Number(e.target.value),
                                      batch.availableQuantity
                                    )
                                  )
                                }
                                inputProps={{
                                  min: 1,
                                  max: batch.availableQuantity,
                                }}
                                size="small"
                                sx={{ borderRadius: 2 }}
                                InputProps={{
                                  sx: { fontSize: '1rem' }
                                }}
                                InputLabelProps={{
                                  sx: { fontSize: '1rem' }
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontSize="0.8rem"
                              >
                                Max: {batch.availableQuantity}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <TextField
                                fullWidth
                                select
                                label="Condition"
                                value={batch.condition}
                                onChange={(e) =>
                                  updateSelectedBatch(
                                    batch.batchId,
                                    "condition",
                                    e.target.value
                                  )
                                }
                                size="small"
                                sx={{ borderRadius: 2 }}
                                InputProps={{
                                  sx: { fontSize: '1rem' }
                                }}
                                InputLabelProps={{
                                  sx: { fontSize: '1rem' }
                                }}
                              >
                                {conditionTypes.map((condition) => (
                                  <MenuItem
                                    key={condition.value}
                                    value={condition.value}
                                    sx={{ fontSize: '1rem' }}
                                  >
                                    {condition.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                label="Reason"
                                value={batch.reason}
                                onChange={(e) =>
                                  updateSelectedBatch(
                                    batch.batchId,
                                    "reason",
                                    e.target.value
                                  )
                                }
                                placeholder="Reason for return..."
                                size="small"
                                sx={{ borderRadius: 2 }}
                                InputProps={{
                                  sx: { fontSize: '1rem' }
                                }}
                                InputLabelProps={{
                                  sx: { fontSize: '1rem' }
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                              <Typography
                                variant="body1"
                                color="success.main"
                                align="right"
                                fontSize="1rem"
                                fontWeight="bold"
                              >
                                Credit: ₹
                                {(batch.returnQuantity * batch.costPrice).toFixed(
                                  2
                                )}
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleBatchSelection(batch, false)}
                                sx={{ borderRadius: 2, fontSize: '0.9rem' }}
                              >
                                Remove
                              </Button>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}

                      <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                        <Typography variant="body1" fontSize="1.1rem" fontWeight="bold">
                          Total Credit Amount: ₹{calculateReturnTotal().toFixed(2)}
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Return Details */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontSize="1.25rem">
                      Return Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Return Date"
                          value={newReturn.returnDate}
                          onChange={(e) =>
                            setNewReturn((prev) => ({
                              ...prev,
                              returnDate: e.target.value,
                            }))
                          }
                          InputLabelProps={{ 
                            shrink: true,
                            sx: { fontSize: '1rem' }
                          }}
                          sx={{ borderRadius: 2 }}
                          InputProps={{
                            sx: { fontSize: '1rem' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Return Reason"
                          value={newReturn.reason}
                          onChange={(e) =>
                            setNewReturn((prev) => ({
                              ...prev,
                              reason: e.target.value,
                            }))
                          }
                          placeholder="Overall reason for return..."
                          sx={{ borderRadius: 2 }}
                          InputProps={{
                            sx: { fontSize: '1rem' }
                          }}
                          InputLabelProps={{
                            sx: { fontSize: '1rem' }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Notes"
                          value={newReturn.notes}
                          onChange={(e) =>
                            setNewReturn((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                          placeholder="Additional notes about this return..."
                          sx={{ borderRadius: 2 }}
                          InputProps={{
                            sx: { fontSize: '1rem' }
                          }}
                          InputLabelProps={{
                            sx: { fontSize: '1rem' }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReturnDialog(false)} sx={{ borderRadius: 2, fontSize: '1rem' }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateReturn}
              variant="contained"
              disabled={selectedBatches.length === 0 || !newReturn.reason}
              sx={{ 
                borderRadius: 2,
                fontSize: '1rem',
                py: 1.5
              }}
            >
              Create Return (₹{calculateReturnTotal().toFixed(2)})
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Status Dialog */}
        <StatusUpdateDialog
          open={statusDialog}
          onClose={() => setStatusDialog(false)}
          selectedReturn={selectedReturn}
          statusUpdate={statusUpdate}
          setStatusUpdate={setStatusUpdate}
          onUpdateStatus={handleUpdateStatus}
          getStatusIcon={getStatusIcon}
        />

        {/* View Return Dialog */}
        <Dialog
          open={viewDialog}
          onClose={() => setViewDialog(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>
            <Typography variant="h5" fontSize="1.5rem" fontWeight="bold">
              Return Details - {selectedReturn?.returnNumber}
            </Typography>
            <Chip
              label={selectedReturn?.status}
              color={getStatusColor(selectedReturn?.status)}
              sx={{ ml: 2, borderRadius: 2, fontSize: '0.9rem' }}
            />
          </DialogTitle>
          <DialogContent>
            {selectedReturn && (
              <ReturnDetailsView
                returnRecord={selectedReturn}
                getStatusColor={getStatusColor}
                getStatusIcon={getStatusIcon}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialog(false)} sx={{ borderRadius: 2, fontSize: '1rem' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

// Enhanced Return Details Component
const ReturnDetailsView = ({ returnRecord, getStatusColor, getStatusIcon }) => {
  const getStatusInfo = (status) => {
    const info = {
      draft: {
        description: "Return is in draft mode and can be edited",
        actions: "Can be submitted or cancelled",
        stockImpact: "No stock impact",
        color: "default",
      },
      pending: {
        description: "Waiting for approval",
        actions: "Can be approved, rejected, or cancelled",
        stockImpact: "No stock impact yet",
        color: "warning",
      },
      approved: {
        description: "Return approved - stock has been reduced",
        actions: "Can be completed or cancelled",
        stockImpact: "Stock quantities reduced",
        color: "info",
      },
      completed: {
        description: "Return completed - credit note issued",
        actions: "No further actions",
        stockImpact: "Stock reduced, credit note created",
        color: "success",
      },
      rejected: {
        description: "Return was rejected",
        actions: "Can be resubmitted",
        stockImpact: "No stock impact",
        color: "error",
      },
      cancelled: {
        description: "Return was cancelled",
        actions: "Can be resubmitted",
        stockImpact: "No stock impact",
        color: "default",
      },
    };
    return info[status] || info.draft;
  };

  const statusInfo = getStatusInfo(returnRecord.status);

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      {/* Status Information Card */}
      <Grid item xs={12}>
        <Card variant="outlined" sx={{ borderColor: statusInfo.color, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
              {getStatusIcon(returnRecord.status)}
              <Typography variant="h6" fontSize="1.25rem">
                Status:{" "}
                {returnRecord.status.charAt(0).toUpperCase() +
                  returnRecord.status.slice(1)}
              </Typography>
              <Chip
                label={returnRecord.status}
                color={getStatusColor(returnRecord.status)}
                size="small"
                sx={{ borderRadius: 2, fontSize: '0.9rem' }}
              />
            </Box>
            <Typography variant="body1" color="text.secondary" fontSize="1rem">
              {statusInfo.description}
            </Typography>
            <Typography variant="body1" display="block" sx={{ mt: 1 }} fontSize="0.95rem">
              <strong>Available Actions:</strong> {statusInfo.actions}
            </Typography>
            <Typography variant="body1" display="block" fontSize="0.95rem">
              <strong>Stock Impact:</strong> {statusInfo.stockImpact}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Credit Note Display */}
      {returnRecord.creditNoteNumber && (
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" color="text.secondary" fontSize="1.1rem">
            Credit Note Number
          </Typography>
          <Typography variant="h6" color="primary" fontSize="1.25rem">
            {returnRecord.creditNoteNumber}
          </Typography>
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="text.secondary" fontSize="1.1rem">
          Return Number
        </Typography>
        <Typography variant="h6" fontSize="1.25rem">{returnRecord.returnNumber}</Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="text.secondary" fontSize="1.1rem">
          Return Date
        </Typography>
        <Typography fontSize="1.1rem">
          {new Date(returnRecord.returnDate).toLocaleDateString()}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="text.secondary" fontSize="1.1rem">
          Total Credit
        </Typography>
        <Typography variant="h5" color="success.main" fontSize="1.5rem">
          ₹{returnRecord.totalCreditAmount?.toLocaleString()}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography variant="subtitle1" color="text.secondary" fontSize="1.1rem">
          Reason
        </Typography>
        <Typography fontSize="1.1rem">{returnRecord.reason}</Typography>
      </Grid>

      {/* Success Alert for Completed Returns */}
      {returnRecord.status === "completed" && (
        <Grid item xs={12}>
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            <Typography variant="subtitle1" fontSize="1.1rem">
              ✅ Return Completed & Credit Note Issued
            </Typography>
            <Typography variant="body1" fontSize="1rem">
              Credit Note: <strong>{returnRecord.creditNoteNumber}</strong> •
              Amount:{" "}
              <strong>
                ₹{returnRecord.totalCreditAmount?.toLocaleString()}
              </strong>{" "}
              • Payment recorded as supplier credit.
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Return Items */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom fontSize="1.25rem">
          Return Items
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>Medicine</TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>Batch Number</TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>PO Reference</TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>Condition</TableCell>
                <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 600 }}>Quantity</TableCell>
                <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 600 }}>Unit Cost</TableCell>
                <TableCell align="right" sx={{ fontSize: '1rem', fontWeight: 600 }}>Total Credit</TableCell>
                <TableCell sx={{ fontSize: '1rem', fontWeight: 600 }}>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returnRecord.items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="subtitle1" fontSize="1rem">
                      {item.medicine?.name || "N/A"}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" fontSize="0.9rem">
                      {item.medicine?.genericName}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '1rem' }}>{item.batchNumber}</TableCell>
                  <TableCell sx={{ fontSize: '1rem' }}>{item.purchaseOrder?.poNumber || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.condition.replace("_", " ")}
                      size="small"
                      color={
                        conditionTypes.find((c) => c.value === item.condition)
                          ?.color || "default"
                      }
                      sx={{ borderRadius: 2, fontSize: '0.8rem' }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '1rem' }}>{item.quantity}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '1rem' }}>
                    ₹{item.unitCost?.toFixed(2)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle1" color="success.main" fontSize="1rem" fontWeight="bold">
                      ₹{item.totalCredit?.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '1rem' }}>{item.reason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {returnRecord.notes && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" color="text.secondary" fontSize="1.1rem">
            Notes
          </Typography>
          <Typography fontSize="1.1rem">{returnRecord.notes}</Typography>
        </Grid>
      )}
    </Grid>
  );
};

// Enhanced Return Table Component with Status Rules and Pagination
const ReturnTable = ({
  returns,
  tabValue,
  onView,
  onStatusUpdate,
  getStatusColor,
  getStatusIcon,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredReturns = returns.filter((returnRecord) => {
    if (tabValue === 0) return true;
    if (tabValue === 1) return returnRecord.status === "draft";
    if (tabValue === 2) return returnRecord.status === "pending";
    if (tabValue === 3) return returnRecord.status === "approved";
    if (tabValue === 4) return returnRecord.status === "completed";
    return true;
  });

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginated data
  const paginatedReturns = filteredReturns.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Status-based action rules
  const getStatusActions = (status) => {
    const actions = {
      draft: [
        {
          action: "submit",
          label: "Submit",
          color: "primary",
          variant: "outlined",
        },
      ],
      pending: [
        {
          action: "approve",
          label: "Approve",
          color: "success",
          variant: "contained",
        },
        {
          action: "reject",
          label: "Reject",
          color: "error",
          variant: "outlined",
        },
      ],
      approved: [
        {
          action: "complete",
          label: "Complete",
          color: "primary",
          variant: "contained",
        },
        {
          action: "cancel",
          label: "Cancel",
          color: "default",
          variant: "outlined",
        },
      ],
      completed: [], // No actions for completed
      rejected: [
        {
          action: "resubmit",
          label: "Resubmit",
          color: "warning",
          variant: "outlined",
        },
      ],
      cancelled: [
        {
          action: "resubmit",
          label: "Resubmit",
          color: "warning",
          variant: "outlined",
        },
      ],
    };
    return actions[status] || [];
  };

  const handleQuickAction = (returnRecord, action) => {
    const statusMap = {
      submit: "pending",
      approve: "approved",
      reject: "rejected",
      complete: "completed",
      cancel: "cancelled",
      resubmit: "pending",
    };

    onStatusUpdate(returnRecord, statusMap[action]);
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold" fontSize="1.25rem">
          {tabValue === 0
            ? "All"
            : tabValue === 1
            ? "Draft"
            : tabValue === 2
            ? "Pending"
            : tabValue === 3
            ? "Approved"
            : "Completed"}{" "}
          Returns ({filteredReturns.length})
        </Typography>

        {filteredReturns.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body1" fontSize="1.1rem">
              No return records found.
            </Typography>
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Return Number</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Reason</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Total Credit</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Credit Note</TableCell>
                    <TableCell sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReturns.map((returnRecord) => {
                    const statusActions = getStatusActions(returnRecord.status);

                    return (
                      <TableRow key={returnRecord._id} hover>
                        <TableCell>
                          <Typography variant="subtitle1" fontWeight="bold" fontSize="1.1rem">
                            {returnRecord.returnNumber}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '1rem' }}>
                          {new Date(returnRecord.returnDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body1"
                            noWrap
                            sx={{ maxWidth: 200 }}
                            fontSize="1rem"
                          >
                            {returnRecord.reason}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="success.main"
                            fontSize="1.1rem"
                          >
                            ₹{returnRecord.totalCreditAmount?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(returnRecord.status)}
                            label={returnRecord.status}
                            size="small"
                            color={getStatusColor(returnRecord.status)}
                            sx={{ borderRadius: 2, fontSize: '0.9rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          {returnRecord.creditNoteNumber ? (
                            <Chip
                              label={returnRecord.creditNoteNumber}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ borderRadius: 2, fontSize: '0.9rem' }}
                            />
                          ) : (
                            <Typography variant="body1" color="text.secondary" fontSize="1rem">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {/* View Details - Always Available */}
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => onView(returnRecord)}
                                color="primary"
                                sx={{ borderRadius: 2 }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>

                            {/* Status-based Actions */}
                            {statusActions.map((action) => (
                              <Tooltip key={action.action} title={action.label}>
                                <Button
                                  size="small"
                                  variant={action.variant}
                                  color={action ? action.color : "primary"}
                                  onClick={() =>
                                    handleQuickAction(returnRecord, action.action)
                                  }
                                  sx={{ 
                                    minWidth: "auto", 
                                    px: 1, 
                                    borderRadius: 2,
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {action.label}
                                </Button>
                              </Tooltip>
                            ))}

                            {/* Advanced Status Update (All except completed) */}
                            {returnRecord.status !== "completed" && (
                              <Tooltip title="Advanced Status Update">
                                <IconButton
                                  size="small"
                                  onClick={() => onStatusUpdate(returnRecord)}
                                  color="warning"
                                  sx={{ borderRadius: 2 }}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredReturns.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '1rem'
                }
              }}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Status Update Dialog Component
const StatusUpdateDialog = ({
  open,
  onClose,
  selectedReturn,
  statusUpdate,
  setStatusUpdate,
  onUpdateStatus,
  getStatusIcon,
}) => {
  const getAllowedStatuses = (currentStatus) => {
    const allowedTransitions = {
      draft: ["pending", "cancelled"],
      pending: ["approved", "rejected", "cancelled"],
      approved: ["completed", "cancelled"],
      rejected: ["pending"],
      completed: [],
      cancelled: ["pending"],
    };
    return allowedTransitions[currentStatus] || [];
  };

  const allowedStatuses = selectedReturn
    ? getAllowedStatuses(selectedReturn.status)
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Typography variant="h5" fontSize="1.5rem" fontWeight="bold">
          Update Return Status
        </Typography>
        {selectedReturn && (
          <Typography variant="body1" color="text.secondary" fontSize="1.1rem">
            Current: {selectedReturn.returnNumber} ({selectedReturn.status})
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {selectedReturn && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={getStatusIcon(selectedReturn.status)}
                sx={{ borderRadius: 2 }}
              >
                <Typography variant="body1" fontSize="1.1rem">
                  Updating: {selectedReturn.returnNumber}
                </Typography>
                <Typography variant="body1" fontSize="1.1rem" fontWeight="bold">
                  Current Status: {selectedReturn.status}
                </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1rem' }}>New Status</InputLabel>
                <Select
                  value={statusUpdate.status}
                  onChange={(e) =>
                    setStatusUpdate((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  label="New Status"
                  sx={{ borderRadius: 2 }}
                  inputProps={{
                    sx: { fontSize: '1rem' }
                  }}
                >
                  {allowedStatuses.map((status) => (
                    <MenuItem key={status} value={status} sx={{ fontSize: '1rem' }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {getStatusIcon(status)}
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {allowedStatuses.length === 0 && (
                <Typography variant="body1" color="error" sx={{ mt: 1 }} fontSize="1rem">
                  No status transitions allowed from {selectedReturn.status}
                </Typography>
              )}
            </Grid>

            {/* Stock Impact Warning */}
            {statusUpdate.status === "approved" &&
              selectedReturn.status === "pending" && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    <Typography variant="body1" fontSize="1.1rem">
                      <strong>Stock Impact:</strong> Approving this return will
                      reduce stock quantities.
                    </Typography>
                  </Alert>
                </Grid>
              )}

            {/* Credit Note Section */}
            {statusUpdate.status === "completed" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Credit Note Number"
                  value={statusUpdate.creditNoteNumber}
                  onChange={(e) =>
                    setStatusUpdate((prev) => ({
                      ...prev,
                      creditNoteNumber: e.target.value,
                    }))
                  }
                  placeholder="CN-XXXXXX (auto-generated if empty)"
                  helperText="Leave empty for auto-generation"
                  sx={{ borderRadius: 2 }}
                  InputProps={{
                    sx: { fontSize: '1rem' }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '1rem' }
                  }}
                  FormHelperTextProps={{
                    sx: { fontSize: '0.9rem' }
                  }}
                />
              </Grid>
            )}

            {/* Stock Restoration Info */}
            {(statusUpdate.status === "cancelled" ||
              statusUpdate.status === "rejected") &&
              selectedReturn.items?.some((item) => item.stockReduced) && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body1" fontSize="1.1rem">
                      <strong>Stock Restoration:</strong> This will restore
                      previously reduced stock quantities.
                    </Typography>
                  </Alert>
                </Grid>
              )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Status Notes"
                value={statusUpdate.notes}
                onChange={(e) =>
                  setStatusUpdate((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
                placeholder="Reason for status change..."
                sx={{ borderRadius: 2 }}
                InputProps={{
                  sx: { fontSize: '1rem' }
                }}
                InputLabelProps={{
                  sx: { fontSize: '1rem' }
                }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ borderRadius: 2, fontSize: '1rem' }}>Cancel</Button>
        <Button
          onClick={onUpdateStatus}
          variant="contained"
          disabled={!statusUpdate.status || allowedStatuses.length === 0}
          sx={{ 
            borderRadius: 2,
            fontSize: '1rem',
            py: 1.5
          }}
        >
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierReturns;